import React from "react";
import { RenderSecure } from "../../util/script/RenderSecure";
import useOrgProfile from "../../util/accounting/orgProfile";
import { buildDocument, tableHtml, openPrintWindow } from "../../util/accounting/printDocument";

/**
 * ExportBar — Print / Excel (CSV) / PDF for any table on screen.
 *
 * All three outputs carry the organisation letterhead (badge, name, address,
 * TIN) built from the company profile, so a printed or exported report is
 * identifiable on its own. Print and PDF share one document builder; the only
 * difference is that Print goes straight to the browser's print dialog.
 *
 * Props:
 *   data:        array of row objects to export
 *   columns:     [{ key, label, numeric? }] — same shape as DataTable columns
 *   filename:    base filename without extension  (default: "export")
 *   pdfTitle:    document title on the printout   (default: "Report")
 *   subtitle:    period / filter description shown under the title
 *   meta:        extra header facts, e.g. ["Financial Year: 2025/26"]
 *   totals:      optional { [columnKey]: value } row rendered as the table foot
 *   note:        closing note (defaults to the configured invoice footer)
 *   landscape:   force landscape paper (auto for wide tables)
 *   printCode:   RenderSecure permission code for the print button
 *   exportCode:  RenderSecure permission code for the Excel + PDF buttons
 *   className:   extra classes on the wrapper div
 *
 * printCode/exportCode default to "" meaning always visible.
 */
const ExportBar = ({
  data = [],
  columns = [],
  filename = "export",
  pdfTitle = "Report",
  subtitle,
  meta = [],
  totals,
  note,
  landscape,
  printCode = "",
  exportCode = "",
  className = "",
}) => {
  const org = useOrgProfile();

  const buildHtml = () =>
    buildDocument({
      org,
      title: pdfTitle,
      subtitle,
      meta: [...meta, `${data.length} record${data.length === 1 ? "" : "s"}`],
      body: tableHtml(columns, data, { totals }),
      note,
      // Wide tables are unreadable on portrait A4.
      landscape: landscape !== undefined ? landscape : columns.length > 6,
    });

  // ── CSV / EXCEL ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!data.length) return;
    const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    // Identify the file for whoever opens the spreadsheet later.
    const banner = [
      [org.name],
      [[org.address, org.phone, org.email].filter(Boolean).join(" | ")],
      [org.tin ? `TIN: ${org.tin}` : ""],
      [pdfTitle + (subtitle ? ` — ${subtitle}` : "")],
      [`Generated: ${new Date().toLocaleString("en-GB")}`],
      [],
    ]
      .filter((row) => row.length === 0 || row.some((c) => c))
      .map((row) => row.map(csvEscape).join(","));

    const rows = [
      ...banner,
      columns.map((c) => csvEscape(c.label)).join(","),
      ...data.map((row) => columns.map((c) => csvEscape(row[c.key])).join(",")),
      ...(totals ? [columns.map((c) => csvEscape(totals[c.key] ?? "")).join(",")] : []),
    ];

    const blob = new Blob(["﻿" + rows.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    }); // BOM so Excel reads UTF-8
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── PDF (branded document, user saves via the print dialog) ────────────────
  const exportPDF = () => {
    if (!data.length) return;
    openPrintWindow(buildHtml());
  };

  // ── PRINT (same document, straight to the print dialog) ───────────────────
  const handlePrint = () => {
    if (!data.length) {
      window.print(); // nothing tabular to brand — fall back to the page itself
      return;
    }
    openPrintWindow(buildHtml(), { autoPrint: true });
  };

  return (
    <div className={`d-flex gap-2 align-items-center flex-wrap ${className}`}>
      <RenderSecure code={printCode}>
        <button className="btn btn-sm btn-outline-secondary" onClick={handlePrint} title="Print with the organisation letterhead">
          <i className="fa-solid fa-print me-1"></i>Print
        </button>
      </RenderSecure>

      <RenderSecure code={exportCode}>
        <button className="btn btn-sm btn-outline-success" onClick={exportCSV}
          title="Export to CSV / Excel" disabled={!data.length}>
          <i className="fa-solid fa-file-excel me-1"></i>Excel
        </button>
        <button className="btn btn-sm btn-outline-danger" onClick={exportPDF}
          title="Export to PDF" disabled={!data.length}>
          <i className="fa-solid fa-file-pdf me-1"></i>PDF
        </button>
      </RenderSecure>
    </div>
  );
};

export default ExportBar;
